import React, {BaseSyntheticEvent, ChangeEvent, MouseEvent, useEffect, useState} from 'react';
import styled from 'styled-components';
import {connect} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
    logOut,
    retrieveToken,
    retrieveTokenFromMask,
    retrieveTokenFromSaml,
    retrieveTokenFromToken,
} from '../../../actions/session';
import {WerfenColor, White} from '../../Constants/ColorChart';
import Logo from '../Logo/Logo';
import {Col, Container, Row} from 'react-bootstrap';
import {keyReturnHandler} from '../../Utils/utils';
import isElectron from 'is-electron';
import {REDIRECT_TIMEOUT, SamlLoginURL} from '../../Constants/AppData';
import pkg from '../../../../../package.json';
import {useMatomo} from '@datapunt/matomo-tracker-react';
import LoginTermsModal from './LoginTermsModal';
import LoginMicrosoft from './LoginMicrosoft';
import LoginErrorMessage from './LoginErrorMessage';
import LoginRegisterAndForgot from './LoginRegisterAndForgot';
import LoginRedirect from './LoginRedirect';
import LoginButton from './LoginButton';
import LoginLoaderRetrieving from './LoginLoaderRetrieving';
import LoginStartSession from './LoginStartSession';
import LoginUsername from './LoginUsername';
import LoginPassword from './LoginPassword';
import LoginReasons from './LoginReasons';
import LoginTerms from "./LoginTerms";

const SAML_REQUIRED_ERROR_CODE = 'saml_required';

const LogoWrapper = styled.div`
  position: relative;
  max-width: 400px;
  width: 100%;
  display: block;
  margin: 0 auto;
  box-sizing: content-box;
  padding: 15px 0 35px 0;
  @media (max-width: 992px) {
    width: 200px;
  }
`;

const WrapperDiv = styled.div`
  min-height: 100vh;
  background: ${WerfenColor};
  position: relative;
  display: flex;
  align-items: center;
  p {
    color: ${White};
    text-align: center;
    a {
      display: block;
      text-align: center;
      color: ${White};
      font-weight: bold;
    }
  }
`;

type LoginProps = {
    isNetworkAvailable: boolean;
    isServerReachable: boolean;
    session: any;
    retrieveToken: (user: string, pass: string) => void;
    retrieveTokenFromToken: (loginToken: string) => void;
    retrieveTokenFromMask: (maskToken: string) => void;
    retrieveTokenFromSaml: (samlToken: string) => void;
};

const Login: React.FC<LoginProps> = (props) => {
    const history = useHistory();
    const [user, setUser] = useState('');
    const [pass, setPass] = useState('');
    const [reason, setReason] = useState('');
    const [integradoresErrorMessage, setIntegradoresErrorMessage] = useState('');
    const [checked, setTermsChecked] = useState(false);
    const [termsPopUpOpen, openTermsPopUp] = useState(false);
    const [disabledLoginClicked, setDisabledLoginClicked] = useState(false);
    const conditionForIntegradores = process.env.REACT_APP_SEE_ACCESS_LOG === '1';
    const {pushInstruction} = useMatomo();
    const canShowMicrosoftButton: boolean = !isElectron() && process.env.REACT_APP_SEE_ENTRA_LOGIN === '1';
    const isLoadSamlToken: boolean = props.session.isLoadSamlToken;
    const errorCode: string | null = props.session.errorCode;
    // The Microsoft sign-in message only makes sense when the Microsoft button is shown.
    const errorMessage: string | null = errorCode === SAML_REQUIRED_ERROR_CODE && !canShowMicrosoftButton
        ? 'Forbidden'
        : props.session.errorMessage;
    const isRetrieveToken: boolean = props.session.isRetrievingToken;
    const isNotRetrieveToken: boolean = !props.session.isRetrievingToken;
    const canShowLoginButton: boolean = isNotRetrieveToken
        && process.env.REACT_APP_LOGIN_BY_TOKEN !== '1'
        && props.isNetworkAvailable
        && !isLoadSamlToken;
    const canShowLoginInputs: boolean = process.env.REACT_APP_LOGIN_BY_TOKEN !== '1'
        && props.isNetworkAvailable
        && !isLoadSamlToken;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setDisabledLoginClicked(false);
        setIntegradoresErrorMessage('');
        setTermsChecked(!checked);
    };

    const handleChangeInTextArea = (event: ChangeEvent<HTMLTextAreaElement>) => {
        event.preventDefault();
        setDisabledLoginClicked(false);
        setIntegradoresErrorMessage('');
        setReason(event.target.value);
    };

    const handleLoginClick = (event: BaseSyntheticEvent) => {
        event.preventDefault();
        setDisabledLoginClicked(true);
        setIntegradoresErrorMessage('Para poder iniciar sesión, primero debe escribir en el formulario las razones por las que accede a la web y marcar que ha leído los términos y condiciones.');
    };

    const handleSamlLoginClick = (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        pushInstruction('setDocumentTitle', 'Saml Login Action ');

        window.location.href = SamlLoginURL;
    };

    const handleSubmit = (event: BaseSyntheticEvent) => {
        event.preventDefault();
        const application = isElectron() ? '- Desktop' : '- Web';
        setIntegradoresErrorMessage('');
        setDisabledLoginClicked(false);
        pushInstruction('setUserId', user);
        pushInstruction('setDocumentTitle', 'Login Action ');
        pushInstruction('trackEvent', 'WDOCS ' + application, 'login', user);
        if (conditionForIntegradores) {
            localStorage.setItem('integradoresReasons', reason);
            pushInstruction('trackEvent', 'WDOCS ' + application, 'Reasons', reason);
        }

        props.retrieveToken(user, pass);
    };

    const retrieveToken = (object: { user: string, pass: string }) => {
        const application = isElectron() ? '- Desktop' : '- Web';
        pushInstruction('setUserId', object.user);
        pushInstruction('setDocumentTitle', 'Login Action ');
        pushInstruction('trackEvent', 'WDOCS ' + application, 'login', object.user);
        props.retrieveToken(object.user, object.pass);
    };

    const loginByToken = (object: { loginToken: string }) => {
        props.retrieveTokenFromToken(object.loginToken);
    };

    const loginByMask = (object: { maskToken: string }) => {
        props.retrieveTokenFromMask(object.maskToken);
    };

    const loginBySaml = (object: { samlToken: string }) => {
        props.retrieveTokenFromSaml(object.samlToken);
    };

    useEffect(() => {
        document.title = process.env.REACT_APP_SITE_APP_NAME + ' - v' + pkg.version;
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const loginData = urlParams.get('t');
        const loginToken = urlParams.get('l');
        const maskToken = urlParams.get('mask');
        const samlToken = urlParams.get('saml');
        const isThereToken = window.localStorage.getItem('token') !== null;

        if (loginData !== null) {
            const decodedString = atob(loginData);
            const loginArray = decodedString.split('///');
            const name = loginArray[0];
            const pass = loginArray[1];
            retrieveToken({user: name, pass: pass});
            return;
        }

        if (loginToken !== null) {
            history.replace({search: ''});
            loginByToken({loginToken});
            return;
        }

        if (maskToken !== null) {
            history.replace({search: ''});
            loginByMask({maskToken});
            return;
        }

        if (samlToken !== null) {
            history.replace({search: ''});
            loginBySaml({samlToken});
            return;
        }

        if (process.env.REACT_APP_LOGIN_BY_TOKEN === '1' && !isThereToken) {
            setTimeout(() => window.location.replace(process.env.REACT_APP_REDIRECT_FOR_LOGIN), REDIRECT_TIMEOUT);
            return;
        }

        if (window.location.pathname && (!isElectron() || window.location.protocol !== 'file:')) {
            logOut();
            history.push(window.location.pathname);
            return;
        }

        if (isElectron()) {
            history.push('/');
            return;
        }

        logOut();
        window.location.replace('/');
    }, []);

    return (
        <WrapperDiv className={'LoginFormContainer'}>
            <Container>
                <Row>
                    <Col lg={{span: 6, offset: 3}} xs={{span: 10, offset: 1}}>
                        <LogoWrapper><Logo/></LogoWrapper>
                        {
                            canShowMicrosoftButton &&
                          <LoginMicrosoft
                            handleClick={handleSamlLoginClick}
                            disabled={isLoadSamlToken}
                          />
                        }
                        {
                            canShowLoginInputs &&
                          <>
                            <LoginUsername
                              user={user}
                              handleChange={(event) => setUser(event.target.value)}
                              handleKeyPress={(event) => keyReturnHandler(event, () => handleSubmit(event))}
                            />
                            <LoginPassword
                              pass={pass}
                              handleChange={(event) => setPass(event.target.value)}
                              handleKeyPress={(event) => keyReturnHandler(event, () => handleSubmit(event))}
                            />
                              {
                                  conditionForIntegradores &&
                                <>
                                  <LoginReasons
                                    reason={reason}
                                    handleChange={handleChangeInTextArea}
                                    handleKeyPress={(event) => keyReturnHandler(event, () => handleSubmit(event))}
                                  />
                                  <LoginTerms
                                    checked={checked}
                                    handleChange={handleChange}
                                    handleClick={() => openTermsPopUp(true)}
                                  />
                                </>
                              }
                          </>
                        }
                        {
                            process.env.REACT_APP_LOGIN_BY_TOKEN !== '1' &&
                            !props.isNetworkAvailable &&
                          <LoginStartSession/>
                        }
                        {
                            process.env.REACT_APP_LOGIN_BY_TOKEN === '1' &&
                            isNotRetrieveToken &&
                          <LoginRedirect/>
                        }
                    </Col>
                </Row>
                {
                    isRetrieveToken &&
                  <LoginLoaderRetrieving/>
                }
                {
                    canShowLoginButton &&
                  <>
                    <LoginButton
                      submitClick={handleSubmit}
                      loginClick={handleLoginClick}
                      reason={reason}
                      checked={checked}
                    />
                      {
                          !isElectron() &&
                        <LoginRegisterAndForgot/>
                      }
                  </>
                }
                {
                    errorMessage &&
                  <LoginErrorMessage message={errorMessage}/>
                }
                {
                    disabledLoginClicked &&
                  <LoginErrorMessage message={integradoresErrorMessage}/>
                }
                {
                    termsPopUpOpen &&
                  <Row>
                    <LoginTermsModal
                      termsPopUpOpen={termsPopUpOpen}
                      openTermsPopUp={openTermsPopUp}
                    />
                  </Row>
                }
            </Container>
        </WrapperDiv>
    );
};

type LoginStageProps = {
    sensors: {
        isNetworkAvailable: boolean;
        isServerReachable: boolean;
    },
    session: {
        isRetrievingToken: boolean;
        isLoadSamlToken: boolean;
        errorMessage: string;
        errorCode: string | null;
    }
}

type mapStateToPropsType = {
    isNetworkAvailable: boolean;
    isServerReachable: boolean;
    session: {
        isRetrievingToken: boolean;
        isLoadSamlToken: boolean;
        errorMessage: string | null;
        errorCode: string | null;
    }
}

const mapStateToProps = (state: LoginStageProps): mapStateToPropsType => ({
    isNetworkAvailable: state.sensors.isNetworkAvailable,
    isServerReachable: state.sensors.isServerReachable,
    session: state.session,
});

export default connect(mapStateToProps, {
    retrieveToken,
    retrieveTokenFromToken,
    retrieveTokenFromMask,
    retrieveTokenFromSaml,
})(Login);
